'use client'

import { Map } from '@vis.gl/react-google-maps'
import Image from 'next/image'
import type { FC } from 'react'
import styles from './RefineEntrance.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import { getImageUrl } from '@/tools/image-service/client'
import { CheckCircleIcon, IconBlob } from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import { pinIconPaths } from '@/user/address-book/ui/constants'
import { SET_ENTRANCE_ZOOM_LEVEL } from '@/user/address-book/ui/constants'

interface Props {
  latitude: number
  longitude: number
  onSetEntrance: () => void
}

export const RefineEntrance: FC<Props> = ({
  latitude,
  longitude,
  onSetEntrance: navigateSetEntrance,
}) => {
  const { t } = useLocalisation()
  const { hasRefinedEntrance } = useAddressBookContext()

  const initialPinImagePath = getImageUrl({ source: pinIconPaths.pinRefineEntrance })
  const refinedPinImagePath = getImageUrl({ source: pinIconPaths.pinIcon })

  const handleMapClick = () => {
    navigateSetEntrance()
  }

  return (
    <div className={styles.refineEntrance}>
      <h3 className={styles.title}>{t('address-details.refine-entrance.title')}</h3>

      <div className={styles.subtitle}>
        {hasRefinedEntrance ? (
          <>
            <IconBlob icon={CheckCircleIcon} size="s" type="positive" />
            <p>{t('address-details.refine-entrance.feedback')}</p>
          </>
        ) : (
          <p>{t('address-details.refine-entrance.subtitle')}</p>
        )}
      </div>

      <div
        className={styles.mapContainer}
        data-testid="refine-entrance-map-container"
        onClick={handleMapClick}
      >
        <Map
          defaultCenter={{ lat: latitude, lng: longitude }}
          defaultZoom={SET_ENTRANCE_ZOOM_LEVEL}
          draggableCursor={'pointer'}
          fullscreenControl={false}
          gestureHandling={'none'}
          id={'refine-entrance-map'}
          mapTypeControl={false}
          streetViewControl={false}
          zoomControl={false}
        />

        {hasRefinedEntrance ? (
          <div className={styles.refinedOverlay}>
            <Image
              alt="Pin icon"
              className={styles.refinedPin}
              height={68}
              src={refinedPinImagePath}
              width={86}
            />
          </div>
        ) : (
          <div className={styles.overlay}>
            <Image alt="Pin icon with arrows" height={120} src={initialPinImagePath} width={151} />
          </div>
        )}
      </div>
    </div>
  )
}
